import React from 'react';
import Script from 'next/script';
import { useLocale } from '@mentimeter/i18n';
import type { FaqEntryT } from '@mentimeter/plans-content';
import { ClientIntlProvider } from 'src/ClientIntlProvider';
import { renderToString } from 'react-dom/server';
import { useFaqConfig } from '../hooks/useFaqConfig';

const isFaqEntry = (
  faqEntryOrFalse: FaqEntryT | false,
): faqEntryOrFalse is FaqEntryT => Boolean(faqEntryOrFalse);

export function FaqStructuredData({
  monthlyBillingIsSupported,
  isEducation = false,
}: {
  monthlyBillingIsSupported: boolean;
  isEducation?: boolean;
}) {
  const locale = useLocale();
  const faqData = useFaqConfig({ isEducation, monthlyBillingIsSupported });
  if (!faqData) return null;

  const faqEntries = faqData.filter(isFaqEntry);

  const data = {
    '@context': 'https://schema.org',
    name: 'FAQ',
    '@type': 'FAQPage',
    mainEntity: faqEntries.map((entry) => {
      return {
        '@type': 'Question',
        name: entry.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: IntlWrapper(renderToString(entry.answer || '') || '', locale),
        },
      };
    }),
  };

  return (
    <Script
      id="structured-data"
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

function removeReactRenderingArtefacts(text: string) {
  return text.replaceAll('<!-- -->', '').trim();
}

function IntlWrapper(element: React.ReactElement | string, locale: string) {
  const wrappedElement =
    typeof element === 'string' ? <span>{element}</span> : element;
  let renderOutput = '';

  try {
    renderOutput = renderToString(
      <ClientIntlProvider locale={locale}>{wrappedElement}</ClientIntlProvider>,
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error rendering internationalized output:', error);
  }

  return removeReactRenderingArtefacts(renderOutput);
}
